import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { mediaHttp } from '@/lib/api/http-client';
import { parseResponse } from '@/lib/api/parse-response';
import { toDeliveryJob, toJobPlan } from './job-mappers';
import { MEDIA_CAMPAIGN_QUERY_KEYS } from './query-keys';
import {
  connectorAdvertisersSchema,
  connectorPagesSchema,
  jobHistorySchema,
  jobListSchema,
  jobPlanSchema,
  jobSchema,
} from './schemas';

const JOB_PAGE_SIZE = 100;

/**
 * Jobs for a project, newest first.
 *
 * Fetched as one page rather than paginated: the toolbar searches and filters the
 * loaded set client-side, and a project holds jobs in the tens.
 */
export const deliveryJobsQueryOptions = (projectId: string) =>
  queryOptions({
    queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.jobs(projectId),
    queryFn: async ({ signal }) => {
      const res = await mediaHttp.get({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: '/api/v1/delivery-jobs',
        query: { projectId, pageSize: JOB_PAGE_SIZE, sortBy: 'created_at', sortOrder: 'DESC' },
        signal,
      });

      return parseResponse(jobListSchema, res).items.map(toDeliveryJob);
    },
  });

/**
 * How often to ask again while a publish is running on a worker.
 *
 * A publish takes minutes, so this is about noticing it finished rather than
 * tracking progress; a tighter interval would just add requests for a status that
 * changes once.
 */
const PUBLISHING_POLL_MS = 3000;

/**
 * @param awaitingBackgroundRun keeps the poll going for work that does not move the
 * job status — a dry run reports without changing what the plan is, so nothing on
 * the job would otherwise say it is still running.
 */
export const deliveryJobQueryOptions = (
  jobId: string,
  projectId: string,
  awaitingBackgroundRun = false,
) =>
  queryOptions({
    queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.job(projectId, jobId),
    enabled: Boolean(jobId),
    queryFn: async ({ signal }) => {
      const res = await mediaHttp.get({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/delivery-jobs/${jobId}`,
        signal,
      });

      return toDeliveryJob(parseResponse(jobSchema, res));
    },
    // The run is on a worker, so the status is the only way to learn it finished.
    // Polling stops the moment it leaves `publishing`, including on a reload part
    // way through someone else's run.
    refetchInterval: (query) =>
      query.state.data?.status === 'publishing' ||
      query.state.data?.isReadingSource ||
      awaitingBackgroundRun
        ? PUBLISHING_POLL_MS
        : false,
  });

export const jobPlanQueryOptions = (jobId: string) =>
  queryOptions({
    queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.jobPlan(jobId),
    enabled: Boolean(jobId),
    // The plan carries the bound account's health, which changes on another screen —
    // so returning from reconnecting one has to pick the new state up.
    refetchOnWindowFocus: true,
    queryFn: async ({ signal }) => {
      const res = await mediaHttp.get({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/delivery-jobs/${jobId}/plan`,
        signal,
      });

      return toJobPlan(parseResponse(jobPlanSchema, res));
    },
  });

/** One entry of a job's append-only audit trail. */
export interface JobHistoryEntry {
  id: string;
  actorId: string;
  action: string;
  reason: string | null;
  nodeId: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export const jobHistoryQueryOptions = (jobId: string) =>
  queryOptions({
    queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.jobHistory(jobId),
    queryFn: async ({ signal }) => {
      const res = await mediaHttp.get({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/delivery-jobs/${jobId}/history`,
        signal,
      });

      return parseResponse(jobHistorySchema, res);
    },
    enabled: jobId !== '',
  });

/** A page an account can publish ads as. */
export interface ConnectorPage {
  id: string;
  name: string;
}

/**
 * The pages a connector can publish as.
 *
 * Answers an empty list for a platform that attributes ads to the account rather than a
 * page, which is most of them, so a caller can show the chooser only where there is one.
 */
export interface ConnectorAdvertiser {
  id: string;
  name: string;
}

/** A chooser's worth of options, plus whether the platform has the concept at all. */
export interface ConnectorAdvertiserList {
  items: ConnectorAdvertiser[];
  supported: boolean;
}

/**
 * The advertisers a connector can publish into.
 *
 * Answers `supported: false` for a platform whose account is itself the advertiser, which
 * is most of them, so a caller can show the chooser only where there is a choice.
 *
 * The search is the platform's, not ours: one partner answers with 904 advertisers, so
 * an unsearched call takes only the first page and typing asks the platform again.
 */
export const connectorAdvertisersQueryOptions = (connectorId: string | null, search = '') =>
  queryOptions({
    queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.connectorAdvertisers(connectorId ?? '', search),
    enabled: Boolean(connectorId),
    // Keeps the previous page of names on screen while a new search is in flight, so the
    // list does not blink empty between keystrokes.
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }): Promise<ConnectorAdvertiserList> => {
      const query = search ? `?search=${encodeURIComponent(search)}` : '';
      const res = await mediaHttp.get({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/platform-management/connectors/${connectorId}/advertisers${query}`,
        signal,
      });

      const data = parseResponse(connectorAdvertisersSchema, res);
      return { items: data.items ?? [], supported: data.supported ?? false };
    },
  });

export const connectorPagesQueryOptions = (connectorId: string | null) =>
  queryOptions({
    queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.connectorPages(connectorId ?? ''),
    enabled: Boolean(connectorId),
    queryFn: async ({ signal }) => {
      const res = await mediaHttp.get({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/platform-management/connectors/${connectorId}/pages`,
        signal,
      });

      const data = parseResponse(connectorPagesSchema, res);
      return { items: data.items ?? [], supported: data.supported ?? false };
    },
  });
